import React, { useCallback, useEffect, useRef } from 'react';
import {
  DIRECTOR_CROSS_SUGGEST_BANNER_CLOSE_EVENT,
  DIRECTOR_CROSS_SUGGEST_BANNER_OPEN_EVENT,
  closeDirectorCrossSuggestBanner,
  getDirectorCrossSuggestBannerContext
} from '../utils/directorCrossSuggestBannerBus';

/**
 * Fixed dismissible strip (same UX as before). DOM-only visibility — no EditFilings re-renders.
 * z-index 900: below inline validation / indicators (~1000+) so errors stay readable.
 */
export const DirectorCrossSuggestBanner: React.FC = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const messageRef = useRef<HTMLSpanElement>(null);
  const applyHandlerRef = useRef<(() => void) | null>(null);

  const close = useCallback(() => {
    if (rootRef.current) {
      rootRef.current.style.display = 'none';
    }
    applyHandlerRef.current = null;
  }, []);

  const open = useCallback(() => {
    const ctx = getDirectorCrossSuggestBannerContext();
    if (!ctx) return;

    applyHandlerRef.current = ctx.onApply;
    if (messageRef.current) {
      messageRef.current.textContent = ctx.message;
    }
    if (rootRef.current) {
      rootRef.current.style.display = 'flex';
    }
  }, []);

  const handleApply = useCallback(() => {
    const fn = applyHandlerRef.current;
    closeDirectorCrossSuggestBanner();
    if (fn) fn();
  }, []);

  const handleDismiss = useCallback(() => {
    closeDirectorCrossSuggestBanner();
  }, []);

  useEffect(() => {
    document.addEventListener(DIRECTOR_CROSS_SUGGEST_BANNER_OPEN_EVENT, open);
    document.addEventListener(DIRECTOR_CROSS_SUGGEST_BANNER_CLOSE_EVENT, close);
    return () => {
      document.removeEventListener(DIRECTOR_CROSS_SUGGEST_BANNER_OPEN_EVENT, open);
      document.removeEventListener(DIRECTOR_CROSS_SUGGEST_BANNER_CLOSE_EVENT, close);
    };
  }, [open, close]);

  return (
    <div
      ref={rootRef}
      data-director-suggest-banner="true"
      role="status"
      className="director-cross-suggest-banner"
      style={{
        display: 'none',
        position: 'fixed',
        left: '50%',
        bottom: '20px',
        transform: 'translateX(-50%)',
        zIndex: 900,
        maxWidth: 'min(560px, calc(100vw - 32px))',
        padding: '12px 16px',
        background: '#0f172a',
        color: '#f8fafc',
        borderRadius: '8px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        alignItems: 'center',
        gap: '12px',
        fontSize: '14px',
        lineHeight: 1.4,
        flexWrap: 'wrap'
      }}
    >
      <span ref={messageRef} style={{ flex: '1 1 220px' }} />
      <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
        <button
          type="button"
          onClick={handleApply}
          style={{
            padding: '6px 14px',
            background: '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          Copy from other section
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          style={{
            padding: '6px 12px',
            background: 'transparent',
            color: '#e2e8f0',
            border: '1px solid #64748b',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};
